import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LangProvider } from './i18n';
import './responsive.css';

// Public pages
import Home           from './pages/Home';
import Login          from './pages/Login';
import Register       from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import Terms    from './pages/Terms';
import Pricing  from './pages/Pricing';
import ApiDocs  from './pages/ApiDocs';
import Services from './pages/Services';
import Faq      from './pages/Faq';
import Accounts from './pages/Accounts';
import Partner  from './pages/Partner';
import Subscription from './pages/Subscription';

// Client pages
import Dashboard    from './pages/Dashboard';
import Orders       from './pages/Orders';
import NewOrder     from './pages/NewOrder';
import Deposit      from './pages/Deposit';
import Wallet       from './pages/Wallet';
import ApiKey       from './pages/ApiKey';
import Subscriptions from './pages/Subscriptions';
import Zenith        from './pages/Zenith';

// Admin pages
import AdminClients       from './pages/admin/AdminClients';
import AdminServices      from './pages/admin/AdminServices';
import AdminOrders        from './pages/admin/AdminOrders';
import AdminPayments      from './pages/admin/AdminPayments';
import AdminSettings      from './pages/admin/AdminSettings';
import AdminBonuses        from './pages/admin/AdminBonuses';
import AdminTickets        from './pages/admin/AdminTickets';
import AdminSubscriptions from './pages/admin/AdminSubscriptions';
import AdminAnalytics     from './pages/admin/AdminAnalytics';
import AdminTiers         from './pages/admin/AdminTiers';
import Tickets             from './pages/Tickets';

import Layout from './components/Layout';

const isLoggedIn = () => !!localStorage.getItem('token');
const isAdmin    = () => {
    try { return JSON.parse(localStorage.getItem('client'))?.is_admin; }
    catch { return false; }
};

// Guard: redirect to /login if not authenticated
const Priv = ({ children }) => isLoggedIn() ? children : <Navigate to="/login" replace />;

// Guard: redirect to /dashboard if not admin
const Adm  = ({ children }) => isAdmin() ? children : <Navigate to="/dashboard" replace />;

// Guard: redirect to /dashboard if already logged in (for login/register pages)
const Guest = ({ children }) => !isLoggedIn() ? children : <Navigate to="/dashboard" replace />;

// Page view tracker — fires on every route change
function PageTracker() {
    const location = useLocation();
    useEffect(() => {
        fetch('/public/track', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: location.pathname, referrer: document.referrer || null }),
        }).catch(() => {});
    }, [location.pathname]);
    return null;
}

export default function App() {
    return (
        <LangProvider>
        <BrowserRouter>
            <PageTracker />
            <Routes>
                {/* Public pages */}
                <Route path="/"         element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/terms"    element={<Terms />} />
                <Route path="/pricing"  element={<Pricing />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="/faq"      element={<Faq />} />
                <Route path="/partner" element={<Partner />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/register"         element={<Guest><Register /></Guest>} />
                <Route path="/login"            element={<Guest><Login /></Guest>} />
                <Route path="/forgot-password"  element={<ForgotPassword />} />
                <Route path="/reset-password"   element={<ResetPassword />} />

                {/* Protected client area */}
                <Route path="/" element={<Priv><Layout /></Priv>}>
                    <Route path="dashboard"      element={<Dashboard />} />
                    <Route path="orders"         element={<Orders />} />
                    <Route path="subscriptions"  element={<Subscriptions />} />
                    <Route path="tickets"        element={<Tickets />} />
                    <Route path="new-order"      element={<NewOrder />} />
                    <Route path="deposit"        element={<Deposit />} />
                    <Route path="wallet"         element={<Wallet />} />
                    <Route path="api-key"        element={<ApiKey />} />
                    <Route path="zenith"         element={<Zenith />} />

                    {/* Admin-only */}
                    <Route path="admin/clients"  element={<Adm><AdminClients /></Adm>} />
                    <Route path="admin/services" element={<Adm><AdminServices /></Adm>} />
                    <Route path="admin/orders"   element={<Adm><AdminOrders /></Adm>} />
                    <Route path="admin/subscriptions" element={<Adm><AdminSubscriptions /></Adm>} />
                    <Route path="admin/payments" element={<Adm><AdminPayments /></Adm>} />
                    <Route path="admin/settings" element={<Adm><AdminSettings /></Adm>} />
                    <Route path="admin/bonuses"        element={<Adm><AdminBonuses /></Adm>} />
                    <Route path="admin/tickets"        element={<Adm><AdminTickets /></Adm>} />
                    <Route path="admin/analytics"      element={<Adm><AdminAnalytics /></Adm>} />
                    <Route path="admin/tiers"          element={<Adm><AdminTiers /></Adm>} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
        </LangProvider>
    );
}
